import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreOutlined"
import ForkLeftOutlined from "@mui/icons-material/ForkLeftOutlined"
import HourglassEmptyOutlined from "@mui/icons-material/HourglassEmptyOutlined"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined"
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Divider from "@mui/material/Divider"
import Fab from "@mui/material/Fab"
import InputAdornment from "@mui/material/InputAdornment"
import Paper from "@mui/material/Paper"
import Popover from "@mui/material/Popover"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { useState } from "react"
import { DragPreviewImage, useDrag } from "react-dnd"
import { Panel, useReactFlow } from "reactflow"

import Column from "../../../../common/components/Column"
import { BaseGlassMixin } from "../../../../common/components/GlassMixin"
import { useIsMobile } from "../../../../common/utils"
import { ConditionFieldStateWithOperator } from "./MultiConditionEditor"
import TimeDelayNodeData from "./react-flow-nodes/time-delay/TimeDelayNodeData"
import NODE_TYPES from "./react-flow-nodes/types"
import { getEdgeFromConnection, getNodeFromDropItem } from "./utils"

export default function TileSidePanel(panelProps) {
  const isMobile = useIsMobile()

  return isMobile ? (
    <MobileTilePopover />
  ) : (
    <DesktopTilePanel panelProps={panelProps} />
  )
}

function DesktopTilePanel({ panelProps }) {
  const { searchText, setSearchText } = useSearchTextState()
  return (
    <Panel
      position="top-right"
      {...panelProps}
      style={{
        width: "310px",
        height: "530px",
        padding: "0px",
        background: BaseGlassMixin.background,
        boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(8px)",
        borderRadius: "16px",
      }}
    >
      <TileSidePanelCommon
        searchText={searchText}
        setSearchText={setSearchText}
      >
        {TITLED_TILE_SECTIONS.map((tileSection, i) => (
          <TitledTileSection key={i} title={tileSection.title}>
            <FilteredTileSection
              tileSection={tileSection}
              searchText={searchText}
            />
          </TitledTileSection>
        ))}
      </TileSidePanelCommon>
    </Panel>
  )
}

function MobileTilePopover() {
  const [mobilePopoverAnchorElem, setMobilePopoverAnchorElem] = useState(null)
  const { searchText, setSearchText } = useSearchTextState()
  const popoverOpen = Boolean(mobilePopoverAnchorElem)

  function closePopover() {
    setMobilePopoverAnchorElem(null)
  }

  return (
    <>
      <Popover
        anchorEl={mobilePopoverAnchorElem}
        open={popoverOpen}
        onClose={closePopover}
        anchorOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        <TileSidePanelCommon
          searchText={searchText}
          setSearchText={setSearchText}
        >
          <TileSection>
            {TITLED_TILE_SECTIONS.map((tileSection, i) => (
              <FilteredTileSection
                tileSection={tileSection}
                searchText={searchText}
              />
            ))}
          </TileSection>
        </TileSidePanelCommon>
      </Popover>
      <Fab
        color="primary"
        sx={{ position: "fixed", bottom: "32px", right: "32px" }}
        onClick={(e) => setMobilePopoverAnchorElem(e.currentTarget)}
      >
        {popoverOpen ? <CloseOutlinedIcon /> : <WidgetsOutlinedIcon />}
      </Fab>
    </>
  )
}

function TileSidePanelCommon({ searchText, setSearchText, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        py: { xs: 2, sm: 3 },
        boxShadow: "none",
        width: "310px",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Column sx={{ px: { xs: 1, sm: 1 } }}>
        <Typography variant="h5" gutterBottom>
          Actions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Click and drag blocks to the canvas to build your workflow
        </Typography>
        <TextField
          label="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon variant="g500" />
              </InputAdornment>
            ),
          }}
          fullWidth
          sx={{
            mt: 3,
          }}
        />
      </Column>
      <Divider sx={{ mt: 3 }} />
      <Column sx={{ px: 1, mt: 3 }}>{children}</Column>
    </Paper>
  )
}

function FilteredTileSection({ tileSection, searchText }) {
  return tileSection.tiles
    .filter((tileData) =>
      tileData.title.toLowerCase().includes(searchText.toLowerCase())
    )
    .map((tileData, i) => <Tile {...tileData} key={i} />)
}

function useSearchTextState() {
  const [searchText, setSearchText] = useState("")
  return {
    searchText,
    setSearchText,
  }
}

function TileSection({ children }) {
  return (
    <Grid container spacing={1}>
      {children}
    </Grid>
  )
}

function TitledTileSection({ title, children }) {
  const [expanded, setExpanded] = useState(true)
  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={(e, expanded) => setExpanded(expanded)}
      sx={{ outline: "none" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon variant="g400" />}>
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TileSection>{children}</TileSection>
      </AccordionDetails>
    </Accordion>
  )
}

function Tile({ defaultNode, title, icon: Icon }) {
  const nodeItem = {
    ...defaultNode,
    data: { ...defaultNode.data, label: title },
  }
  const [{ isDragging }, dragRef, preview] = useDrag(() => ({
    type: ITEM_TYPE_TILE,
    options: {
      dropEffect: "copy",
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: nodeItem,
  }))
  // detect clicking without dragging
  const [tileClicked, setTileClicked] = useState(false)
  const reactFlowInstance = useReactFlow()

  function handleMouseUpEvent() {
    if (tileClicked) {
      function getLeafEdge(leafNode, handleID) {
        return edges.filter(
          (edge) =>
            edge.source === leafNode.id && edge.sourceHandle === handleID
        )[0]
      }
      const nodes = reactFlowInstance
        .getNodes()
        .filter((node) => node.type !== NODE_TYPES.note)
      const edges = reactFlowInstance.getEdges()
      const entryPositionX = nodes[0].position.x
      let leafNode = nodes[0]
      // connect both yes and no branches of split node even if not last node
      const leafYesNoSplitNode = nodes.filter(
        (node) => node.type === NODE_TYPES.yesNoSplit
      )[0]
      if (leafYesNoSplitNode) {
        const yesEdge = getLeafEdge(leafYesNoSplitNode, "yes")
        const noEdge = getLeafEdge(leafYesNoSplitNode, "no")
        if (!yesEdge || !noEdge) {
          leafNode = leafYesNoSplitNode
        }
      }
      const newNode = getNodeFromDropItem(nodeItem, () => ({
        x: entryPositionX,
        y: leafNode.position.y + 150,
      }))
      let sourceHandle = "bottom-handle"
      let addEdge = true
      if (leafNode.type === NODE_TYPES.yesNoSplit) {
        const yesEdge = getLeafEdge(leafNode, "yes")
        const noEdge = getLeafEdge(leafNode, "no")
        const conditionXDistance = 150
        if (yesEdge && noEdge) {
          addEdge = false
        } else if (yesEdge) {
          sourceHandle = "no"
          newNode.position.x = leafNode.position.x + conditionXDistance
        } else {
          sourceHandle = "yes"
          newNode.position.x = leafNode.position.x - conditionXDistance
        }
      }
      reactFlowInstance.addNodes(newNode)
      if (addEdge) {
        reactFlowInstance.addEdges(
          getEdgeFromConnection({
            source: leafNode.id,
            sourceHandle,
            target: newNode.id,
            targetHandle: "top-handle",
          })
        )
      }
    }
    setTileClicked(false)
  }

  return (
    <Grid xs={6}>
      <DragPreviewImage
        connect={preview}
        src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
      />
      <TileColumn
        ref={dragRef}
        sx={{
          backgroundColor: (theme) =>
            isDragging ? theme.palette.colors.gray50 : "inherit",
        }}
        onMouseDown={() => setTileClicked(true)}
        onMouseMove={() => setTileClicked(false)}
        onMouseUp={handleMouseUpEvent}
      >
        <Icon variant={isDragging ? "b500" : "g600"} />
        <Typography variant="body1" sx={{ mt: 1 }}>
          {title}
        </Typography>
      </TileColumn>
    </Grid>
  )
}

class TileData {
  constructor(title, icon, defaultNode) {
    this.title = title
    this.icon = icon
    this.defaultNode = defaultNode
  }
}

class TitledTileSectionData {
  constructor(title, tiles) {
    this.title = title
    this.tiles = tiles
  }
}

export const ITEM_TYPE_TILE = "tile"

class PartialNode {
  constructor(type, data = { label: "", editorData: null }) {
    this.type = type
    this.data = data
  }
}

const TITLED_TILE_SECTIONS = [
  new TitledTileSectionData("Messages", [
    new TileData("Email", EmailOutlinedIcon, new PartialNode(NODE_TYPES.email)),
  ]),
  new TitledTileSectionData("Tasks", [
    new TileData(
      "Task",
      AssignmentOutlinedIcon,
      new PartialNode(NODE_TYPES.task)
    ),
  ]),
  new TitledTileSectionData("Delays", [
    new TileData(
      "Time delay",
      HourglassEmptyOutlined,
      new PartialNode(NODE_TYPES.timeDelay, {
        editorData: new TimeDelayNodeData(),
      })
    ),
  ]),
  new TitledTileSectionData("Flow", [
    new TileData(
      "Yes/no split",
      ForkLeftOutlined,
      new PartialNode(NODE_TYPES.yesNoSplit, {
        editorData: ConditionFieldStateWithOperator.getDefaultState(),
      })
    ),
  ]),
  new TitledTileSectionData("Other", [
    new TileData(
      "Add note",
      StickyNote2Outlined,
      new PartialNode(NODE_TYPES.note, { editorData: "" })
    ),
  ]),
]

const TileColumn = styled(Column)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  width: "126px",
  height: "72px",
  border: `1px solid ${theme.palette.colors.gray200}`,
  borderRadius: "6px",
}))
